import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Download, FileSpreadsheet, TrendingUp, TrendingDown } from 'lucide-react';
import { fmt, computeKPIs } from '../../lib/accountingStore';
import { apiInvoices, apiCustomers, apiExpenses, apiAccounts, apiBills } from '../../lib/api';
import { invoiceOutstanding } from '../../lib/invoiceItems';
import { ProfitLossStatement, BalanceSheetStatement } from './books/Statements';

const ARAging = ({ s }) => {
  const today = new Date();
  const buckets = { current: [], '1-30': [], '31-60': [], '61-90': [], '90+': [] };
  s.invoices.filter(i => i.status !== 'Paid').forEach(i => {
    const due = new Date(i.due);
    const days = Math.floor((today - due) / 86400000);
    if (days < 0) buckets.current.push(i);
    else if (days <= 30) buckets['1-30'].push(i);
    else if (days <= 60) buckets['31-60'].push(i);
    else if (days <= 90) buckets['61-90'].push(i);
    else buckets['90+'].push(i);
  });
  const sum = (arr) => arr.reduce((a,i)=>a+invoiceOutstanding(i),0);
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="p-5 bg-slate-900 text-white"><h3 className="font-bold text-lg">A/R Aging Summary</h3><p className="text-sm text-white/70">Open invoices by age</p></div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
          <tr><th className="px-5 py-3 text-left">Customer</th><th className="px-5 py-3 text-right">Current</th><th className="px-5 py-3 text-right">1–30</th><th className="px-5 py-3 text-right">31–60</th><th className="px-5 py-3 text-right">61–90</th><th className="px-5 py-3 text-right">90+</th><th className="px-5 py-3 text-right">Total</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {s.customers.map(c => {
            const get = (b) => buckets[b].filter(i=>i.customerId===c.id).reduce((a,i)=>a+invoiceOutstanding(i),0);
            const cur = get('current'), b1=get('1-30'), b2=get('31-60'), b3=get('61-90'), b4=get('90+');
            const tot = cur+b1+b2+b3+b4;
            if (!tot) return null;
            return (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-semibold text-slate-900">{c.name}</td>
                <td className="px-5 py-3 text-right tabular-nums">{fmt(cur)}</td>
                <td className="px-5 py-3 text-right tabular-nums">{fmt(b1)}</td>
                <td className="px-5 py-3 text-right tabular-nums">{fmt(b2)}</td>
                <td className="px-5 py-3 text-right tabular-nums">{fmt(b3)}</td>
                <td className="px-5 py-3 text-right tabular-nums text-red-600">{fmt(b4)}</td>
                <td className="px-5 py-3 text-right font-bold tabular-nums">{fmt(tot)}</td>
              </tr>
            );
          })}
          <tr className="bg-slate-50 font-bold">
            <td className="px-5 py-3 text-slate-900">TOTAL</td>
            <td className="px-5 py-3 text-right tabular-nums">{fmt(sum(buckets.current))}</td>
            <td className="px-5 py-3 text-right tabular-nums">{fmt(sum(buckets['1-30']))}</td>
            <td className="px-5 py-3 text-right tabular-nums">{fmt(sum(buckets['31-60']))}</td>
            <td className="px-5 py-3 text-right tabular-nums">{fmt(sum(buckets['61-90']))}</td>
            <td className="px-5 py-3 text-right tabular-nums text-red-600">{fmt(sum(buckets['90+']))}</td>
            <td className="px-5 py-3 text-right tabular-nums text-slate-900">{fmt(sum(buckets.current)+sum(buckets['1-30'])+sum(buckets['31-60'])+sum(buckets['61-90'])+sum(buckets['90+']))}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

export default function AcctReports() {
  const [state, setState] = useState({ invoices: [], customers: [], expenses: [], accounts: [], bills: [] });
  useEffect(() => {
    Promise.all([apiInvoices.list(), apiCustomers.list(), apiExpenses.list(), apiAccounts.list(), apiBills.list()]).then(([inv, cust, exp, acc, bills]) => {
      setState({ invoices: inv || [], customers: cust || [], expenses: exp || [], accounts: acc || [], bills: bills || [] });
    }).catch(() => {});
  }, []);
  const k = computeKPIs(state);
  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Reports</h2><p className="text-slate-500 text-sm">Financial statements computed live from your books.</p></div>
        <Button variant="outline" className="gap-1.5"><Download className="w-4 h-4"/>Export PDF</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-slate-200"><div className="flex items-center gap-2 text-emerald-600"><TrendingUp className="w-4 h-4"/><span className="text-xs font-bold">INCOME</span></div><div className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{fmt(k.income)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="flex items-center gap-2 text-red-600"><TrendingDown className="w-4 h-4"/><span className="text-xs font-bold">EXPENSES</span></div><div className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{fmt(k.expenses)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs font-bold text-blue-600">NET PROFIT</div><div className={`text-2xl font-extrabold mt-1 tabular-nums ${k.profit>=0?'text-emerald-600':'text-red-600'}`}>{fmt(k.profit)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs font-bold text-purple-600">OPEN A/R</div><div className="text-2xl font-extrabold mt-1 tabular-nums text-slate-900">{fmt(k.ar)}</div></Card>
      </div>
      <Tabs defaultValue="pl">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="pl" className="gap-1.5"><FileSpreadsheet className="w-4 h-4"/>Profit &amp; Loss</TabsTrigger>
          <TabsTrigger value="bs" className="gap-1.5"><FileSpreadsheet className="w-4 h-4"/>Balance Sheet</TabsTrigger>
          <TabsTrigger value="ar" className="gap-1.5"><FileSpreadsheet className="w-4 h-4"/>A/R Aging</TabsTrigger>
        </TabsList>
        {/* The same statements as Accounting → Statements, so the two screens never disagree. */}
        <TabsContent value="pl" className="mt-5"><ProfitLossStatement/></TabsContent>
        <TabsContent value="bs" className="mt-5"><BalanceSheetStatement/></TabsContent>
        <TabsContent value="ar" className="mt-5"><ARAging s={state}/></TabsContent>
      </Tabs>
    </div>
  );
}
